import React, { useState } from "react";
import { Building2, ChevronDown, Check, ShieldCheck, Heart, User, HandHeart } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { switchOrg } from "@/lib/api";

const ROLE_ICONS = {
  donor: Heart,
  volunteer: HandHeart,
  member: User,
  organization_admin: ShieldCheck,
  admin: ShieldCheck,
};

const ROLE_COLORS = {
  donor: "bg-rose-50 text-rose-700 border-rose-200",
  volunteer: "bg-amber-50 text-amber-700 border-amber-200",
  member: "bg-blue-50 text-blue-700 border-blue-200",
  organization_admin: "bg-purple-50 text-purple-700 border-purple-200",
};

export default function OrgSwitcher() {
  const { user, org, refresh } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  if (!user || !org) return null;

  const memberships = user.memberships || [
    {
      organization_id: user.organization_id,
      organization_name: org.name,
      roles: user.roles || [user.role || "member"],
    },
  ];

  const userRoles = user.roles || (user.role ? [user.role] : ["member"]);

  const handleSwitch = async (orgId) => {
    if (orgId === org.id || switching) return;
    try {
      setSwitching(true);
      await switchOrg(orgId);
      await refresh();
      setIsOpen(false);
    } catch (err) {
      console.error("Failed to switch organization", err);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="relative inline-block text-left w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors text-foreground shadow-sm"
        data-testid="org-switcher-button"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="p-1.5 rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100">
            <Building2 size={16} />
          </div>
          <div className="text-left min-w-0">
            <div className="truncate text-xs font-semibold text-foreground">{org.name}</div>
            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
              {userRoles.map((r) => {
                const roleLower = r.toLowerCase();
                const badgeColor = ROLE_COLORS[roleLower] || "bg-gray-100 text-gray-700 border-gray-200";
                return (
                  <span
                    key={r}
                    className={`inline-flex items-center px-1.5 py-0.2 text-[10px] font-medium rounded border ${badgeColor}`}
                  >
                    {r.replace("_", " ")}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        <ChevronDown size={14} className={`text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute left-0 right-0 mt-1.5 z-50 rounded-lg bg-popover text-popover-foreground shadow-lg border border-border py-1 overflow-hidden animate-in fade-in-50 zoom-in-95">
            <div className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
              Your Organizations
            </div>
            <div className="max-h-60 overflow-y-auto">
              {memberships.map((m) => {
                const isActive = m.organization_id === org.id;
                return (
                  <button
                    key={m.organization_id}
                    onClick={() => handleSwitch(m.organization_id)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left transition-colors ${
                      isActive ? "bg-indigo-50/70 text-indigo-900 font-semibold" : "hover:bg-accent hover:text-foreground"
                    }`}
                  >
                    <div className="min-w-0 pr-2">
                      <div className="truncate font-medium">{m.organization_name || "Organization"}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Roles: {(m.roles || []).join(", ").replace(/_/g, " ")}
                      </div>
                    </div>
                    {isActive && <Check size={14} className="text-indigo-600 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
